import { useEffect, useRef, useState } from 'react';
import { AuthServiceHelpers } from '../utils/api-helpers/AuthService.helpers';
import { handleUnauthorizedError } from '../utils/authErrorHandler';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;

const ACTIVITY_THROTTLE_MS = 1000;

/**
 * Logs the user out after `idleTimeoutMinutes` without interaction.
 * Timeout comes from GET /auth/public-config (fallback 30).
 */
export function useIdleLogout(enabled: boolean): void {
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(
    AuthServiceHelpers.getDefaultPublicConfig().idleTimeoutMinutes,
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    AuthServiceHelpers.getPublicConfig()
      .then((config) => {
        if (!cancelled) {
          setIdleTimeoutMinutes(config.idleTimeoutMinutes);
        }
      })
      .catch(() => {
        // Keep default; idle logout still works offline from config fetch failure.
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || idleTimeoutMinutes <= 0) {
      return;
    }

    const clearTimer = () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const scheduleLogout = () => {
      clearTimer();
      timerRef.current = setTimeout(
        () => {
          handleUnauthorizedError();
        },
        idleTimeoutMinutes * 60 * 1000,
      );
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) {
        return;
      }
      lastActivityRef.current = now;
      scheduleLogout();
    };

    scheduleLogout();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
    };
  }, [enabled, idleTimeoutMinutes]);
}
